import { useCallback, useSyncExternalStore } from 'react';

/* ─────────────────────────────────────────────────────────────────────────
 *  One store for the two pieces of app chrome that fight over width: the
 *  sidebar (232 ↔ 72) and the docked AI panel (floating ↔ pinned, 450).
 *
 *  Before this, `useAIPanelPinned` kept its own `useState` per caller, so two
 *  components reading "is the panel pinned?" could disagree until a reload.
 *  Everything here goes through one module-level snapshot and
 *  `useSyncExternalStore`, so every subscriber sees the same value on the same
 *  render — and a change in another tab (the `storage` event) lands too.
 *
 *  Persisted (per browser, like the theme):
 *   - aiPanelPinned        — same key as before, so nobody's choice is lost
 *   - sidebarCollapsedPref — null until the person picks; then true/false
 *   - sidebarOpenSections  — which nav sections are expanded
 *  Session-only:
 *   - aiPanelPushing       — the docked panel is open AND pinned right now
 *   - sidebarAutoOverride  — "expand anyway" while auto-collapsed
 * ───────────────────────────────────────────────────────────────────────── */

export const AI_PANEL_PINNED_STORAGE_KEY = 'marble-ai-panel-pinned';
export const SIDEBAR_COLLAPSED_STORAGE_KEY = 'marble-sidebar-collapsed';
export const SIDEBAR_OPEN_SECTIONS_STORAGE_KEY = 'marble-sidebar-open-sections';

/** No stored preference: the sidebar starts expanded at this width and up. */
export const SIDEBAR_DEFAULT_EXPANDED_MIN_WIDTH = 1440;
/** Below this, a pinned (pushing) AI panel collapses the sidebar for room. */
export const SIDEBAR_AUTO_COLLAPSE_MAX_WIDTH = 1536;

export interface LayoutPrefs {
  aiPanelPinned: boolean;
  sidebarCollapsedPref: boolean | null;
  sidebarOpenSections: string[];
  aiPanelPushing: boolean;
  sidebarAutoOverride: boolean;
}

const safeGet = (key: string): string | null => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    /* localStorage unavailable (private mode / quota). */
    return null;
  }
};

const safeSet = (key: string, value: string | null): void => {
  if (typeof window === 'undefined') return;
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    /* The choice still holds for this session; it just won't survive a reload. */
  }
};

export const readStoredAIPanelPinned = (): boolean =>
  safeGet(AI_PANEL_PINNED_STORAGE_KEY) === 'true';

const readCollapsedPref = (): boolean | null => {
  const raw = safeGet(SIDEBAR_COLLAPSED_STORAGE_KEY);
  if (raw === 'true') return true;
  if (raw === 'false') return false;
  return null;
};

const readOpenSections = (): string[] => {
  const raw = safeGet(SIDEBAR_OPEN_SECTIONS_STORAGE_KEY);
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((x): x is string => typeof x === 'string')
      : [];
  } catch {
    return [];
  }
};

const readPersisted = (): Pick<
  LayoutPrefs,
  'aiPanelPinned' | 'sidebarCollapsedPref' | 'sidebarOpenSections'
> => ({
  aiPanelPinned: readStoredAIPanelPinned(),
  sidebarCollapsedPref: readCollapsedPref(),
  sidebarOpenSections: readOpenSections(),
});

let state: LayoutPrefs = {
  ...readPersisted(),
  aiPanelPushing: false,
  sidebarAutoOverride: false,
};

const listeners = new Set<() => void>();

const emit = (): void => listeners.forEach((l) => l());

const setState = (patch: Partial<LayoutPrefs>): void => {
  const next = { ...state, ...patch };
  const changed = (Object.keys(patch) as Array<keyof LayoutPrefs>).some(
    (k) => next[k] !== state[k],
  );
  if (!changed) return;
  state = next;
  emit();
};

const onStorage = (e: StorageEvent): void => {
  if (
    e.key === null ||
    e.key === AI_PANEL_PINNED_STORAGE_KEY ||
    e.key === SIDEBAR_COLLAPSED_STORAGE_KEY ||
    e.key === SIDEBAR_OPEN_SECTIONS_STORAGE_KEY
  ) {
    setState(readPersisted());
  }
};

const subscribe = (listener: () => void): (() => void) => {
  if (listeners.size === 0 && typeof window !== 'undefined') {
    window.addEventListener('storage', onStorage);
  }
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && typeof window !== 'undefined') {
      window.removeEventListener('storage', onStorage);
    }
  };
};

export const getLayoutPrefs = (): LayoutPrefs => state;

export const layoutPrefsActions = {
  setAIPanelPinned(pinned: boolean): void {
    safeSet(AI_PANEL_PINNED_STORAGE_KEY, String(pinned));
    setState({ aiPanelPinned: pinned });
  },
  toggleAIPanelPinned(): void {
    layoutPrefsActions.setAIPanelPinned(!state.aiPanelPinned);
  },
  /** Published by the docked panel; never persisted. Dropping to false also
   * drops any "expand anyway" override, so the next push collapses again. */
  setAIPanelPushing(pushing: boolean): void {
    setState(
      pushing
        ? { aiPanelPushing: true }
        : { aiPanelPushing: false, sidebarAutoOverride: false },
    );
  },
  setSidebarCollapsedPref(collapsed: boolean | null): void {
    safeSet(
      SIDEBAR_COLLAPSED_STORAGE_KEY,
      collapsed === null ? null : String(collapsed),
    );
    setState({ sidebarCollapsedPref: collapsed, sidebarAutoOverride: false });
  },
  setSidebarAutoOverride(override: boolean): void {
    setState({ sidebarAutoOverride: override });
  },
  setSectionOpen(id: string, open: boolean): void {
    const has = state.sidebarOpenSections.includes(id);
    if (has === open) return;
    const next = open
      ? [...state.sidebarOpenSections, id]
      : state.sidebarOpenSections.filter((x) => x !== id);
    safeSet(SIDEBAR_OPEN_SECTIONS_STORAGE_KEY, JSON.stringify(next));
    setState({ sidebarOpenSections: next });
  },
};

/** Test-only: re-read storage and clear session flags. */
export const resetLayoutPrefsForTests = (): void => {
  state = {
    ...readPersisted(),
    aiPanelPushing: false,
    sidebarAutoOverride: false,
  };
  emit();
};

export function useLayoutPrefs(): LayoutPrefs;
export function useLayoutPrefs<T>(select: (s: LayoutPrefs) => T): T;
export function useLayoutPrefs<T>(
  select?: (s: LayoutPrefs) => T,
): T | LayoutPrefs {
  const getSnapshot = useCallback(
    () => (select ? select(state) : state),
    [select],
  );
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/* ── Sidebar collapse rules (pure, so they are table-testable) ──────────── */

export interface CollapseInputs {
  pref: boolean | null;
  viewportWidth: number;
  aiPanelPushing: boolean;
  autoOverride: boolean;
}

/** What the person asked for (or the width-based default when they haven't). */
export const preferredCollapsed = ({
  pref,
  viewportWidth,
}: Pick<CollapseInputs, 'pref' | 'viewportWidth'>): boolean =>
  pref ?? viewportWidth < SIDEBAR_DEFAULT_EXPANDED_MIN_WIDTH;

/** Collapsed for room: a pinned panel is pushing and the screen is narrow. */
export const isAutoCollapsed = ({
  viewportWidth,
  aiPanelPushing,
  autoOverride,
}: Omit<CollapseInputs, 'pref'>): boolean =>
  aiPanelPushing &&
  viewportWidth < SIDEBAR_AUTO_COLLAPSE_MAX_WIDTH &&
  !autoOverride;

/** effectiveCollapsed = preference || (panel pushing && viewport < 1536).
 * The auto part never writes the preference. */
export const effectiveCollapsed = (i: CollapseInputs): boolean =>
  preferredCollapsed(i) || isAutoCollapsed(i);

/** The one collapse/expand toggle. Expanding out of an auto-collapse is a
 * session override, not a saved preference — the saved pref already says
 * "expanded". Everything else is an explicit, persisted choice. */
export const toggleSidebar = (viewportWidth: number): void => {
  const s = getLayoutPrefs();
  const inputs: CollapseInputs = {
    pref: s.sidebarCollapsedPref,
    viewportWidth,
    aiPanelPushing: s.aiPanelPushing,
    autoOverride: s.sidebarAutoOverride,
  };
  if (!effectiveCollapsed(inputs)) {
    layoutPrefsActions.setSidebarCollapsedPref(true);
    return;
  }
  if (preferredCollapsed(inputs)) {
    layoutPrefsActions.setSidebarCollapsedPref(false);
  }
  if (isAutoCollapsed({ ...inputs, autoOverride: false })) {
    layoutPrefsActions.setSidebarAutoOverride(true);
  }
};
